import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewBankAccount, editBankAccount, getBankAccounts } from "../../../redux/features/bank/bankService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB Limit

const BankAccounts = () => {

    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const fileEditInputRef = useRef(null);

    const { isBankAccountsLoading, pageCount, bankAccounts } = useSelector((state) => state.bank.allBankAccounts);
    const { isBankAccountCreating } = useSelector((state) => state.bank.newBankAccount);

    const [formData, setFormData] = useState({
        name: '',
        image: '',
    });

    const [errorMessages, setErrorMessages] = useState({
        name: '',
        image: '',
    });
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [initialBankAccount, setInitialBankAccount] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        file: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllBankAccounts = useCallback(() => {
        dispatch(getBankAccounts({
            skip: skip,
            limit,
            search: searchQuery
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllBankAccounts();
    }, [fetchAllBankAccounts]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateFile = (image) => {
        if (image) {
            if (!image.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, image: "Only image files are allowed." });
                return;
            }

            if (image.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, image: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, image: "" });
            setFormData({ ...formData, image });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };
    // Validate input
    const isValidBankAccount = () => {
        const errors = {};

        // Account Name
        const trimmedAccountName = formData.account_name?.trim();
        if (!trimmedAccountName) {
            errors.account_name = "Account name is required.";
        }

        // Bank Name
        const trimmedBankName = formData.bank_name?.trim();
        if (!trimmedBankName) {
            errors.bank_name = "Bank name is required.";
        }

        // Branch
        const trimmedBranch = formData.branch?.trim();
        if (!trimmedBranch) {
            errors.branch = "Branch is required.";
        }

        // Account Number
        if (!formData.account_number?.trim()) {
            errors.account_number = "Account number is required.";
        } else if (!/^\d{6,20}$/.test(formData.account_number)) {
            errors.account_number = "Account number must be 6–20 digits.";
        }

        // Swift Code (optional but if entered must be valid)
        if (formData.swift_code && !/^[A-Za-z0-9]{4,11}$/.test(formData.swift_code)) {
            errors.swift_code = "Swift code must be 4–11 alphanumeric characters.";
        }

        // Branch Code
        if (!formData.branch_code?.trim()) {
            errors.branch_code = "Branch code is required.";
        } else if (!/^\d{2,10}$/.test(formData.branch_code)) {
            errors.branch_code = "Branch code must be 2–10 digits.";
        }

        // Bank Address
        if (!formData.bank_address?.trim()) {
            errors.bank_address = "Bank address is required.";
        }

        // Currency
        if (!formData.currency?.trim()) {
            errors.currency = "Currency is required.";
        } else if (!/^[A-Z]{3}$/.test(formData.currency)) {
            errors.currency = "Currency must be a 3-letter code (e.g., LKR, USD).";
        }

        // Contact Number
        if (!formData.contact_number?.trim()) {
            errors.contact_number = "Contact number is required.";
        }

        // Reason for Remittance
        if (!formData.reason_for_remittance?.trim()) {
            errors.reason_for_remittance = "Reason for remittance is required.";
        }

        // Image
        if (!formData.image && !formData.image_url) {
            errors.image = "Image is required.";
        }

        // Final result
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        }
        return true;
    };

    const insertNewBankAccount = async () => {
        if (isValidBankAccount(formData)) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewBankAccount(formData)).unwrap();
                setBtnDisable(false);
                setFormData({});
                fetchAllBankAccounts();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit bankAccount
    const handleSelectedBankAccountChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedBankAccount((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateEditFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setEditErrorMsgs({ ...editErrorMsgs, file: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setEditErrorMsgs({ ...editErrorMsgs, file: "File size exceeds 2MB limit." });
                return;
            }

            setEditErrorMsgs({ ...editErrorMsgs, file: "" });
            setSelectedBankAccount({ ...selectedBankAccount, file });
        }
    };

    const handleEditDrop = (event) => {
        setErrorMsg(null);
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateEditFile(selectedFile);
        }
    };

    const handleEditFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateEditFile(selectedFile);
        }
    };

    const handleEditClick = () => {
        setErrorMsg(null);
        fileEditInputRef.current.click();
    };

    const isValidEditBankAccount = () => {
        const errors = {};

        // Account Name
        if (!selectedBankAccount.account_name?.trim()) {
            errors.account_name = "Account name is required.";
        }

        // Bank Name
        if (!selectedBankAccount.bank_name?.trim()) {
            errors.bank_name = "Bank name is required.";
        }

        // Branch
        if (!selectedBankAccount.branch?.trim()) {
            errors.branch = "Branch is required.";
        }

        // Account Number
        if (!selectedBankAccount.account_number?.trim()) {
            errors.account_number = "Account number is required.";
        } else if (!/^\d{6,20}$/.test(selectedBankAccount.account_number)) {
            errors.account_number = "Account number must be 6–20 digits.";
        }

        // Swift Code (optional but validate if present)
        if (selectedBankAccount.swift_code &&
            !/^[A-Za-z0-9]{4,11}$/.test(selectedBankAccount.swift_code)) {
            errors.swift_code = "Swift code must be 4–11 alphanumeric characters.";
        }

        // Branch Code
        if (!selectedBankAccount.branch_code?.trim()) {
            errors.branch_code = "Branch code is required.";
        } else if (!/^\d{2,10}$/.test(selectedBankAccount.branch_code)) {
            errors.branch_code = "Branch code must be 2–10 digits.";
        }

        // Bank Address
        if (!selectedBankAccount.bank_address?.trim()) {
            errors.bank_address = "Bank address is required.";
        }

        // Currency
        if (!selectedBankAccount.currency?.trim()) {
            errors.currency = "Currency is required.";
        } else if (!/^[A-Z]{3}$/.test(selectedBankAccount.currency)) {
            errors.currency = "Currency must be a 3-letter code (e.g., LKR, USD).";
        }

        // Contact Number
        if (!selectedBankAccount.contact_number?.trim()) {
            errors.contact_number = "Contact number is required.";
        }

        // Reason for Remittance
        if (!selectedBankAccount.reason_for_remittance?.trim()) {
            errors.reason_for_remittance = "Reason for remittance is required.";
        }

        // Image
        if (!selectedBankAccount.file && !selectedBankAccount.image_url) {
            errors.file = "Image is required.";
        }

        // Final check
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        }
        return true;
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialBankAccount[key] && key !== 'file'
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editBankAccountDetails = async () => {
        if (!isValidEditBankAccount(selectedBankAccount)) return;

        const data = getChangedValues(selectedBankAccount);
        const hasChanges = Object.keys(data).some(key => key !== 'id');
        if (!hasChanges && !selectedBankAccount.file) {
            return setErrorMsg('There are no changes to update.');
        }

        setEditBtnDisable(true);

        try {
            if (hasChanges) {
                if (selectedBankAccount.file) {
                    const response = await dispatch(editBankAccount({bankData:data, file:selectedBankAccount.file})).unwrap();
                    showToast('success', response.message);
                }
                else {
                    const response = await dispatch(editBankAccount({bankData:data})).unwrap();
                    showToast('success', response.message);
                }
            }

            setSelectedBankAccount(null);
            setInitialBankAccount(null);
            setModalOpen(false);
            fetchAllBankAccounts();
        } catch (error) {
            showToast('error', error.message);
        } finally {
            setEditBtnDisable(false);
        }
    };

    //Activate BankAccount
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const data = {
                id: selectedBankAccountId,
                status: true
            }
            const response = await dispatch(editBankAccount({ bankData: data })).unwrap();
            setSelectedBankAccountId(null);
            setInitialBankAccount(null);
            showToast('success', response.message);
            fetchAllBankAccounts();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
              const data = {
                id: selectedBankAccountId,
                status: false
            }
            const response = await dispatch(editBankAccount({ bankData: data })).unwrap();
            setSelectedBankAccountId(null);
            setInitialBankAccount(null);
            showToast('success', response.message);
            fetchAllBankAccounts();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Bank Account</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Account Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='account_name'
                                        name='account_name'
                                        value={formData.account_name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Account Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.account_name && (
                                        <p className={`text-danger`}>{errorMessages.account_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Bank Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='bank_name'
                                        name='bank_name'
                                        value={formData.bank_name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Account Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.bank_name && (
                                        <p className={`text-danger`}>{errorMessages.bank_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Branch<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='branch'
                                        name='branch'
                                        value={formData.branch}
                                        onChange={handleInputChange}
                                        placeholder='Enter Branch'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.branch && (
                                        <p className={`text-danger`}>{errorMessages.branch}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Account Number<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='account_number'
                                        name='account_number'
                                        value={formData.account_number}
                                        onChange={handleInputChange}
                                        placeholder='Enter Account Number'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.account_number && (
                                        <p className={`text-danger`}>{errorMessages.account_number}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Swift Code<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='swift_code'
                                        name='swift_code'
                                        value={formData.swift_code}
                                        onChange={handleInputChange}
                                        placeholder='Enter Swift Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.swift_code && (
                                        <p className={`text-danger`}>{errorMessages.swift_code}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Branch Code<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='branch_code'
                                        name='branch_code'
                                        value={formData.branch_code}
                                        onChange={handleInputChange}
                                        placeholder='Enter Branch Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.branch_code && (
                                        <p className={`text-danger`}>{errorMessages.branch_code}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Bank Address<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='bank_address'
                                        name='bank_address'
                                        value={formData.bank_address}
                                        onChange={handleInputChange}
                                        placeholder='Enter Bank Address'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.bank_address && (
                                        <p className={`text-danger`}>{errorMessages.bank_address}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Currency<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='currency'
                                        name='currency'
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        placeholder='Enter Currency'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.currency && (
                                        <p className={`text-danger`}>{errorMessages.currency}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Contact Number<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='contact_number'
                                        name='contact_number'
                                        value={formData.contact_number}
                                        onChange={handleInputChange}
                                        placeholder='Enter Contact Number'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.contact_number && (
                                        <p className={`text-danger`}>{errorMessages.contact_number}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Reason for Remittance<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='reason_for_remittance'
                                        name='reason_for_remittance'
                                        value={formData.reason_for_remittance}
                                        onChange={handleInputChange}
                                        placeholder='Enter Reason for Remittance'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.reason_for_remittance && (
                                        <p className={`text-danger`}>{errorMessages.reason_for_remittance}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Image<span className={`required-star`}>*</span>
                                    </label>
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClick}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            style={{ display: "none" }}
                                        />
                                        {(!formData.image) ? <p>Drag & Drop a file here or click to select</p>
                                            :
                                            <div className={`inserted-file`}>
                                                <div className={`file-card`}>
                                                    <img src={URL.createObjectURL(formData.image)} alt='File Type' className={`left-icon`} />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {errorMessages.image && (
                                        <p className={`text-danger`}>{errorMessages.image}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewBankAccount}
                        >
                            {isBankAccountCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Bank Accounts</h1>
                </div>
                <div className={`search-card-body`}>
                    {isBankAccountsLoading ?
                        <TableSkeleton columns={["BankAccount", "Image", "Status", "Actions"]} rowCount={10} height={20} />
                        : bankAccounts.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Bank Name</th>
                                            <th>Image</th>
                                            <th>Account Name</th>
                                            <th>Account Number</th>
                                            <th>Branch</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {bankAccounts?.map((bankAccount) => (
                                            <tr key={bankAccount.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {bankAccount.bank_name}
                                                </td>
                                                <td>
                                                    <img
                                                        src={bankAccount.image_url}
                                                        width={80}
                                                        alt="BankAccount"
                                                    />
                                                </td>
                                                <td>{bankAccount.account_name}</td>
                                                <td>{bankAccount.account_number}</td>
                                                <td>{bankAccount.branch}</td>
                                                <td>{bankAccount.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedBankAccount(bankAccount);
                                                        setInitialBankAccount(bankAccount);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            bankAccount_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={bankAccount.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedBankAccountId(bankAccount.id);
                                                            bankAccount.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{bankAccount.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Bank Account</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form className={styles.coordinatorForm} noValidate>
                                    {/* Account Name */}
                                    <div className="form-input-row md-col">
                                        <div className="form-input-group">
                                            <label htmlFor="account_name" className="form-label">
                                                Account Name<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="account_name"
                                                name="account_name"
                                                value={selectedBankAccount.account_name || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Account Name"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.account_name && (
                                                <p className="text-danger">{editErrorMsgs.account_name}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bank Name */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="bank_name" className="form-label">
                                                Bank Name<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="bank_name"
                                                name="bank_name"
                                                value={selectedBankAccount.bank_name || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Bank Name"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.bank_name && (
                                                <p className="text-danger">{editErrorMsgs.bank_name}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Branch */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="branch" className="form-label">
                                                Branch<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="branch"
                                                name="branch"
                                                value={selectedBankAccount.branch || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Branch Name"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.branch && (
                                                <p className="text-danger">{editErrorMsgs.branch}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Account Number */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="account_number" className="form-label">
                                                Account Number<span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="account_number"
                                                name="account_number"
                                                value={selectedBankAccount.account_number || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Account Number"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.account_number && (
                                                <p className="text-danger">{editErrorMsgs.account_number}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Swift Code */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="swift_code" className="form-label">
                                                Swift Code
                                            </label>
                                            <input
                                                type="text"
                                                id="swift_code"
                                                name="swift_code"
                                                value={selectedBankAccount.swift_code || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Swift Code"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.swift_code && (
                                                <p className="text-danger">{editErrorMsgs.swift_code}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Branch Code */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="branch_code" className="form-label">
                                                Branch Code
                                            </label>
                                            <input
                                                type="text"
                                                id="branch_code"
                                                name="branch_code"
                                                value={selectedBankAccount.branch_code || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Branch Code"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.branch_code && (
                                                <p className="text-danger">{editErrorMsgs.branch_code}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bank Address */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="bank_address" className="form-label">
                                                Bank Address
                                            </label>
                                            <input
                                                type="text"
                                                id="bank_address"
                                                name="bank_address"
                                                value={selectedBankAccount.bank_address || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Bank Address"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.bank_address && (
                                                <p className="text-danger">{editErrorMsgs.bank_address}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Currency */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="currency" className="form-label">
                                                Currency
                                            </label>
                                            <input
                                                type="text"
                                                id="currency"
                                                name="currency"
                                                value={selectedBankAccount.currency || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Currency"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.currency && (
                                                <p className="text-danger">{editErrorMsgs.currency}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contact Number */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="contact_number" className="form-label">
                                                Contact Number
                                            </label>
                                            <input
                                                type="text"
                                                id="contact_number"
                                                name="contact_number"
                                                value={selectedBankAccount.contact_number || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Contact Number"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.contact_number && (
                                                <p className="text-danger">{editErrorMsgs.contact_number}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Reason for Remittance */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="reason_for_remittance" className="form-label">
                                                Reason for Remittance
                                            </label>
                                            <textarea
                                                id="reason_for_remittance"
                                                name="reason_for_remittance"
                                                value={selectedBankAccount.reason_for_remittance || ""}
                                                onChange={handleSelectedBankAccountChange}
                                                placeholder="Enter Reason"
                                                className="form-input-default"
                                            />
                                            {editErrorMsgs.reason_for_remittance && (
                                                <p className="text-danger">
                                                    {editErrorMsgs.reason_for_remittance}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Image Upload */}
                                    <div className="form-input-row">
                                        <div className="form-input-group">
                                            <label htmlFor="image_url" className="form-label">
                                                Image<span className="required-star">*</span>
                                            </label>
                                            <div
                                                onDrop={handleEditDrop}
                                                onDragOver={(event) => event.preventDefault()}
                                                onClick={handleEditClick}
                                                className="drop-edit-input"
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileEditInputRef}
                                                    onChange={handleEditFileChange}
                                                    style={{ display: "none" }}
                                                />
                                                {!selectedBankAccount.image_url && !selectedBankAccount.file ? (
                                                    <p>Drag & Drop a file here or click to select</p>
                                                ) : (
                                                    <div className="inserted-file">
                                                        <div className="file-card">
                                                            <img
                                                                src={
                                                                    selectedBankAccount.file
                                                                        ? URL.createObjectURL(
                                                                            selectedBankAccount.file
                                                                        )
                                                                        : selectedBankAccount.image_url
                                                                }
                                                                alt="File Type"
                                                                className="left-edit-icon"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {editErrorMsgs.file && (
                                                <p className="text-danger">{editErrorMsgs.file}</p>
                                            )}
                                            {errorMsg && <p className="text-danger">{errorMsg}</p>}
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <button
                                        className="btn-primary"
                                        type="button"
                                        disabled={disabledEditBtn}
                                        onClick={editBankAccountDetails}
                                    >
                                        Save
                                    </button>
                                </form>

                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='BankAccount Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the BankAccount?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='BankAccount Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the BankAccount?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default BankAccounts;
